import { useState, useEffect } from 'react';
import { dashboardApi } from '../services/api.js';

function AgentDetailModal({ agent, onClose }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('answered_time');
  const [sortOrder, setSortOrder] = useState('ASC');
  const [dateRange, setDateRange] = useState('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [dateError, setDateError] = useState('');

  const getToday = () => new Date().toISOString().split('T')[0];

  const getRangeDates = (range) => {
    const now = new Date();
    const today = getToday();
    if (range === 'today') {
      return { start: today, end: today };
    } else if (range === 'current_month') {
      return {
        start: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`,
        end: today
      };
    } else if (range === 'previous_month') {
      // FIX: Create separate Date objects to avoid mutation issues
      const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth(), 0); // day 0 of current month = last day of prev month
      return {
        start: `${firstDay.getFullYear()}-${String(firstDay.getMonth() + 1).padStart(2, '0')}-01`,
        end: `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`
      };
    } else if (range === 'custom') {
      return { start: customStart, end: customEnd };
    }
    return { start: today, end: today };
  };

  const validateRange = (start, end) => {
    if (!start || !end) return false;
    const s = new Date(start);
    const e = new Date(end);
    const max = new Date(s);
    max.setMonth(max.getMonth() + 3);
    if (e > max) {
      setDateError('Max 3 months range allowed');
      return false;
    }
    if (s > e) {
      setDateError('Start must be before end');
      return false;
    }
    setDateError('');
    return true;
  };

  const loadCalls = async () => {
    const { start, end } = getRangeDates(dateRange);
    if (!validateRange(start, end)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await dashboardApi.getAgentDetail(agent, {
        start, end, sort_by: sortBy, sort_order: sortOrder
      });
      setCalls(res.data.calls);
    } catch (err) {
      console.error('Failed to load agent details:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCalls();
  }, [agent, sortBy, sortOrder, dateRange, customStart, customEnd]);

  const toggleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
  };

  const rangeButtons = [
    { key: 'today', label: 'Today' },
    { key: 'current_month', label: 'Current Month' },
    { key: 'previous_month', label: 'Previous Month' },
    { key: 'custom', label: 'Custom' }
  ];

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      justifyContent: 'center', alignItems: 'center', zIndex: 1000,
      padding: '20px'
    }} onClick={onClose}>
      <div style={{
        background: 'white', borderRadius: 'var(--radius-lg)',
        padding: '20px', maxWidth: '850px', width: '100%',
        maxHeight: '85vh', overflow: 'auto', boxShadow: 'var(--shadow-lg)'
      }} onClick={e => e.stopPropagation()}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)' }}>
            {agent} — Call Breakdown
          </h2>
          <button onClick={onClose} style={{
            background: 'var(--danger)', color: 'white', border: 'none',
            borderRadius: 'var(--radius-sm)', padding: '6px 14px',
            cursor: 'pointer', fontSize: '13px', fontWeight: '500', fontFamily: 'inherit'
          }}>Close</button>
        </div>

        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center',
          marginBottom: '12px', padding: '10px', background: 'var(--bg-body)',
          borderRadius: 'var(--radius-sm)'
        }}>
          {rangeButtons.map(btn => (
            <button
              key={btn.key}
              onClick={() => setDateRange(btn.key)}
              style={{
                padding: '5px 12px', fontSize: '12px', fontWeight: '500',
                borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
                cursor: 'pointer', fontFamily: 'inherit',
                background: dateRange === btn.key ? 'var(--primary)' : 'white',
                color: dateRange === btn.key ? 'white' : 'var(--text-secondary)'
              }}
            >
              {btn.label}
            </button>
          ))}

          {dateRange === 'custom' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
              <input
                type="date"
                value={customStart}
                onChange={e => setCustomStart(e.target.value)}
                style={{
                  padding: '4px 8px', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)', fontSize: '12px', fontFamily: 'inherit'
                }}
              />
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>to</span>
              <input
                type="date"
                value={customEnd}
                onChange={e => setCustomEnd(e.target.value)}
                style={{
                  padding: '4px 8px', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)', fontSize: '12px', fontFamily: 'inherit'
                }}
              />
            </div>
          )}
        </div>

        {dateError && (
          <div style={{
            background: 'var(--danger-light)', color: 'var(--danger)',
            padding: '8px 12px', borderRadius: 'var(--radius-sm)',
            fontSize: '12px', fontWeight: '500', marginBottom: '10px'
          }}>{dateError}</div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-muted)', fontSize: '14px' }}>Loading...</div>
        ) : calls.length > 0 ? (
          <div style={{ overflowX: 'auto', maxHeight: '50vh', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
                <tr style={{ backgroundColor: 'var(--primary)', color: 'white', cursor: 'pointer' }}>
                  <th onClick={() => toggleSort('callerid')} style={{ padding: '8px', textAlign: 'left', fontSize: '12px' }}>
                    Caller ID {sortBy === 'callerid' && (sortOrder === 'ASC' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => toggleSort('ring_time')} style={{ padding: '8px', textAlign: 'left', fontSize: '12px' }}>
                    Ring Time {sortBy === 'ring_time' && (sortOrder === 'ASC' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => toggleSort('answered_time')} style={{ padding: '8px', textAlign: 'left', fontSize: '12px' }}>
                    Answered {sortBy === 'answered_time' && (sortOrder === 'ASC' ? '↑' : '↓')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {calls.map((call, i) => (
                  <tr key={i} style={{
                    backgroundColor: i % 2 === 0 ? '#f9fafb' : 'white',
                    borderBottom: '1px solid var(--border-light)'
                  }}>
                    <td style={{ padding: '8px' }}>{call.callerid}</td>
                    <td style={{ padding: '8px' }}>{call.ring_time}s</td>
                    <td style={{ padding: '8px' }}>{call.answered_time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-muted)', fontSize: '14px' }}>
            No calls found for selected period.
          </div>
        )}
      </div>
    </div>
  );
}

export default AgentDetailModal;

